//Node Modules
import { useState, useEffect, useId, useRef } from "react";
import { Link } from "react-router-dom";
import { v4 as uuid } from "uuid";
import _ from "lodash";

//BinaryForge Components
import FlowstopItem from "./FlowstopItem";
import { PillItem } from "../helpers";

//3rd Party Components
import { Tooltip } from "primereact/tooltip";

//Atoms

//Helpers
import { getUiDateTime } from "../../helpers/DateUtils";
import { useDeviceOfflineState } from "../../helpers/useDeviceOfflineState";
import { inputDeviceOptions } from "../../config/device";
import { nav } from "../../config/navigation";
import { useTranslation } from "react-i18next";

const AlarmCard = ({ alarmData }) => {
	const { t } = useTranslation();
	const tooltipId = useRef(uuid());
	const getOfflineState = useDeviceOfflineState();
	const errorId = useId();
	const { device: input, outputDevices, updatedAt } = alarmData;

	const [errors, setErrors] = useState([]);
	const [icon, setIcon] = useState();
	const [locationBreadcrumb, setLocationBreadcrumb] = useState();
	const [zoneId, setZoneId] = useState();

	useEffect(() => {
		if (!input) return;
		if (!input.inputDevice) {
			setErrors((prevState) => [...prevState, "Unexpected Alarm - No Input Devices"]);
			setIcon("pi pi-exclamation-triangle warnText");
		} else {
			const deviceObj = _.find(inputDeviceOptions, {
				value: input.inputDevice[0],
			});
			setIcon(deviceObj.icon);
		}

		//zoneBreadcrumb stores { label: "root|...|leaf", key: "rootId|...|leafId" } - the leaf is the device's zone
		const breadcrumb = JSON.parse(input.zoneBreadcrumb || null);
		setLocationBreadcrumb(breadcrumb?.label?.replaceAll("|", " > "));
		setZoneId(breadcrumb?.key?.split("|").filter(Boolean).pop());
	}, [alarmData]);

	// Defensive guard - an alarm with no linked device cannot be rendered (selector should already filter these out)
	if (!input) {
		return (
			<div className="card warnBorder">
				<span>Unexpected Alarm - device not found</span>
			</div>
		);
	}

	const { presence, isOffline, offlineSince } = getOfflineState(input);

	return (
		<div className={`card ${errors.length > 0 && "warnBorder"}`}>
			<Tooltip target={`.customTooltip-${tooltipId.current}`} />

			<header className="flex aItems-center gap-xsmall no-divider marginBottom-xsmall">
				<i className={`${icon} fontColour-error`} />
				<h3>
					<Link to={`${nav.device.base}/${input.id}`}>{input.name}</Link>
				</h3>
				{isOffline && (
					<PillItem
						type="full"
						value={t("device.state.presence.offline")}
						icon="pi-wifi offlineIcon"
						wrapperStyle="grey"
						tooltip={[
							offlineSince && `${t("device.state.offlineSince")}: ${getUiDateTime(offlineSince)}`,
							`${t("device.state.lastSeen")}: ${getUiDateTime(
								presence.lastSeenAtSeconds ?? input.updatedAt,
							)}`,
						]
							.filter(Boolean)
							.join(". ")}
					/>
				)}
			</header>

			<div className="flex aItems-center gap-xsmall">
				<i className="pi pi-map-marker" />
				{input.zone?.label && zoneId ? (
					<Link
						to={`${nav.zones.base}/${nav.zones.management}?zoneId=${zoneId}`}
						className={`customTooltip-${tooltipId.current}`}
						data-pr-tooltip={locationBreadcrumb}
						data-pr-at="right+10 center"
					>
						{input.zone.label}
					</Link>
				) : (
					<span
						className={`customTooltip-${tooltipId.current}`}
						data-pr-tooltip={locationBreadcrumb}
						data-pr-at="right+10 center"
					>
						{input.zone?.label || t("device.state.noLocation")}
					</span>
				)}
			</div>

			<div className="flex aItems-center gap-xsmall">
				<i className="pi pi-clock" />
				<span>{getUiDateTime(updatedAt)}</span>
			</div>

			{errors.length > 0 ? (
				<div className="marginTop-small">
					{errors.map((error) => (
						<span key={errorId}>{error}</span>
					))}
				</div>
			) : (
				outputDevices.length > 0 && (
					<div className="flexVert gapRow-xsmall marginTop-small">
						<span className="fontSize-small fontWeight-bold fontColour-light">
							{t("alarm.card.flowstops")}
						</span>
						{outputDevices.map((outputDevice) => (
							<FlowstopItem key={uuid()} flowstopData={outputDevice} />
						))}
					</div>
				)
			)}
		</div>
	);
};

export default AlarmCard;
